import { ref } from "vue";
import { EventSource } from "eventsource";
import { ApiWrapper } from "@/Utils/ApiWrapper.js";

// One server-sent events connection for the whole app. Components subscribe to the events they care about:
//   const off = EventStream.on('conversation', (event) => { ... });   // later: off()
// The connection opens when the first component subscribes and closes when the last one unsubscribes.
//
// Events:
//   notification  something new for the user's bell
//   conversation  a conversation message was created or changed: {conversationId, messageId, direction, status, errorCode}
//   resync        the server lost track for a moment, so anything could have changed; refetch what's on screen
//   open          the connection is up: {reconnected: true} when it's a reconnect, so anything missed should be refetched
//
// While the connection isn't up (turned off on the server, or reconnecting), `connected` is false and components
// should poll instead.

const RETRY_MS = 5 * 1000;
const WATCHDOG_CHECK_MS = 15 * 1000;
const SILENCE_LIMIT_MS = 50 * 1000;     // the server sends a heartbeat every 20s, so this is two or more missed

const SERVER_EVENTS = ['notification', 'conversation', 'resync', 'heartbeat'];

const connected = ref(false);

const listeners = new Map();            // event name -> Set of functions
let listenerCount = 0;

let eventSource = null;
let retryTimeoutId = null;
let watchdogIntervalId = null;
let lastEventAt = 0;
let connectedBefore = false;
let disabled = false;                   // the server answered 503: streaming isn't enabled there

const emit = (name, data) => {
    listeners.get(name)?.forEach((fn) => {
        try {
            fn(data);
        }
        catch(error) {
            console.error(`Error in '${name}' event handler`, error);
        }
    });
};

const parse = (text) => {
    try {
        return JSON.parse(text);
    }
    catch(error) {
        return {};
    }
};

const scheduleReconnect = () => {
    clearTimeout(retryTimeoutId);
    retryTimeoutId = setTimeout(connect, RETRY_MS);
};

function connect() {
    if(disabled || listenerCount === 0) return;

    eventSource?.close();

    eventSource = new EventSource(`${import.meta.env.VUE_APP_API_ENDPOINT}/notifications/stream/v1`, {
        // Called on every (re)connect, so each attempt gets a fresh token
        fetch: async (input, init) => {
            const config = await ApiWrapper.getConfig();

            return fetch(input, { ...init, headers: { ...init.headers, ...config.headers } });
        },
    });
    const source = eventSource;

    source.onopen = () => {
        lastEventAt = Date.now();
        connected.value = true;

        emit('open', { reconnected: connectedBefore });
        connectedBefore = true;
    };

    SERVER_EVENTS.forEach((name) => {
        source.addEventListener(name, (event) => {
            lastEventAt = Date.now();

            if(name !== 'heartbeat') emit(name, parse(event.data));
        });
    });

    source.onerror = (event) => {
        connected.value = false;

        // Still CONNECTING: a dropped connection the library is already retrying
        if(source.readyState !== EventSource.CLOSED) return;

        // CLOSED: the server refused the stream, so the library won't retry on its own.
        // 503 means streaming isn't enabled on the server, so stay on polling.
        if(event.code === 503) {
            disabled = true;
            return;
        }

        // Anything else (e.g. 401 from an expired token): start over with a new connection
        console.error('Event stream closed, retrying', event.code, event.message);
        scheduleReconnect();
    };
}

// A connection can die without any error (laptop sleep, network change), so reconnect when the heartbeats stop
const watchdog = () => {
    if(connected.value && Date.now() - lastEventAt > SILENCE_LIMIT_MS) {
        console.warn('Event stream went quiet, reconnecting');

        eventSource?.close();
        connected.value = false;
        connect();
    }
};

const start = () => {
    connect();
    watchdogIntervalId = setInterval(watchdog, WATCHDOG_CHECK_MS);
};

const stop = () => {
    clearTimeout(retryTimeoutId);
    clearInterval(watchdogIntervalId);

    eventSource?.close();
    eventSource = null;

    connected.value = false;
    connectedBefore = false;
};

export const EventStream = {
    // true while the stream is up
    connected,

    // Subscribe to an event; returns a function that unsubscribes
    on(name, fn) {
        if(!listeners.has(name)) listeners.set(name, new Set());
        listeners.get(name).add(fn);

        listenerCount++;
        if(listenerCount === 1) start();

        return () => {
            if(!listeners.get(name)?.delete(fn)) return;

            listenerCount--;
            if(listenerCount === 0) stop();
        };
    },
};
